import {
  CheckCircle2,
  ChevronDown,
  Eye,
  Image as ImageIcon,
  Loader2,
  Palette,
  Sparkles,
  Video,
  Zap,
} from "lucide-react";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { useDropdownPosition } from "../../hooks/useDropdownPosition";
import { cn } from "../../utils/cn";

interface AIOptimizerMenuProps {
  onEnhanceText: () => void;
  onGenerateImage: () => void;
  onGenerateVideo: () => void;
  onEnhanceImage?: () => void;
  onGenerateCreative?: () => void;
  onVisualSuggestions: () => void;
  onCheckGrammar?: () => void;
  hasVisualSuggestions?: boolean;
  loadingEnhance: boolean;
  loadingImage: boolean;
  loadingVideo: boolean;
  loadingEnhanceImage: boolean;
  loadingGrammar?: boolean;
  disabled?: boolean;
}

const AIOptimizerMenu: React.FC<AIOptimizerMenuProps> = ({
  onEnhanceText,
  onGenerateImage,
  onGenerateVideo,
  onEnhanceImage,
  onGenerateCreative,
  onVisualSuggestions,
  onCheckGrammar,
  hasVisualSuggestions = false,
  loadingEnhance,
  loadingImage,
  loadingVideo,
  loadingEnhanceImage,
  loadingGrammar = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hasAnyLoading =
    loadingEnhance ||
    loadingImage ||
    loadingVideo ||
    loadingEnhanceImage ||
    loadingGrammar;

  // Posicionamento inteligente do dropdown (igual ao PostCard)
  const position = useDropdownPosition({
    isOpen,
    buttonRef: buttonRef as RefObject<HTMLElement>,
    menuRef: menuRef as RefObject<HTMLElement>,
    preferredPosition: "bottom-right",
    offset: 4,
  });

  // Fechar menu ao clicar fora, tecla Escape e ao rolar a página
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const handleWrapperKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") e.stopPropagation();
  };
  return (
    <div
      className={cn("relative", isOpen ? "z-50" : "z-10")}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleWrapperKeyDown}
      role="group"
      tabIndex={-1}
    >
      {/* Botão trigger - Design compacto */}
      <button
        ref={buttonRef}
        onClick={() => !disabled && !hasAnyLoading && setIsOpen(!isOpen)}
        disabled={disabled || hasAnyLoading}
        className={cn(
          "w-full h-9 flex items-center justify-between gap-2 px-3 rounded-lg",
          "transition-all duration-200 cursor-pointer group",
          "bg-linear-to-r from-purple-50 to-white",
          "border border-purple-200",
          "hover:border-purple-300 hover:shadow-xs",
          "active:scale-[0.98]",
          (disabled || hasAnyLoading) && "opacity-60 cursor-not-allowed",
          isOpen && "border-purple-400 bg-purple-50",
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className={cn(
              "p-1 rounded-md transition-all duration-200",
              "bg-purple-100",
              "group-hover:bg-purple-200",
              hasAnyLoading && "animate-pulse",
            )}
          >
            <Sparkles className="h-3.5 w-3.5 text-purple-700" />
          </div>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
            Otimizar com IA
          </span>
          {hasAnyLoading && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium text-purple-700 bg-purple-100 rounded-sm">
              Processando...
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-purple-600 transition-transform duration-200 shrink-0",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {/* Dropdown - Design compacto */}
      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          tabIndex={0}
          className={cn(
            "absolute w-64 bg-white rounded-lg border border-purple-200 shadow-xl z-100",
            "animate-fade-in",
            "right-0",
          )}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
          }}
          style={{
            ...(position.bottom != null
              ? { bottom: position.bottom, top: "auto" as const }
              : { top: position.top ?? "calc(100% + 4px)" }),
            left: position.left,
            right: position.right !== undefined ? position.right : undefined,
            transform: position.transform,
            maxHeight: position.maxHeight ?? "calc(100vh - 120px)",
            overflowY: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-1.5 space-y-0.5">
            {/* CONTEÚDO */}
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 px-2 py-1 mt-1">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  Conteúdo
                </span>
              </div>
              <button
                onClick={onEnhanceText}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onEnhanceText();
                }}
                disabled={loadingEnhance}
                className={cn(
                  "w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md",
                  "text-gray-700 hover:bg-gray-100/80 hover:text-purple-600 group",
                  loadingEnhance && "opacity-50 cursor-not-allowed",
                )}
              >
                {loadingEnhance ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 shrink-0" />
                ) : (
                  <Zap className="h-4 w-4 text-purple-500 shrink-0" />
                )}
                <span className="text-[13px] font-medium flex-1">
                  Melhorar texto
                </span>
              </button>
              {onCheckGrammar && (
                <button
                  onClick={onCheckGrammar}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    onCheckGrammar();
                  }}
                  disabled={loadingGrammar}
                  className={cn(
                    "w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md",
                    "text-gray-700 hover:bg-gray-100/80 hover:text-green-600 group",
                    loadingGrammar && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {loadingGrammar ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  )}
                  <span className="text-[13px] font-medium flex-1">
                    Verificar gramática
                  </span>
                </button>
              )}
            </div>

            {/* CRIATIVO */}
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 px-2 py-1 mt-1 border-t border-purple-50/50">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  Criativo
                </span>
              </div>
              <button
                onClick={onGenerateImage}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onGenerateImage();
                }}
                disabled={loadingImage}
                className={cn(
                  "w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md",
                  "text-gray-700 hover:bg-gray-100/80 hover:text-blue-600 group",
                  loadingImage && "opacity-50 cursor-not-allowed",
                )}
              >
                {loadingImage ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 shrink-0" />
                ) : (
                  <ImageIcon className="h-4 w-4 text-blue-500 shrink-0" />
                )}
                <span className="text-[13px] font-medium flex-1">
                  Gerar imagem
                </span>
              </button>
              {onGenerateCreative && (
                <button
                  onClick={() => {
                    onGenerateCreative();
                    setIsOpen(false);
                  }}
                  className="w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md text-gray-700 hover:bg-gray-100/80 hover:text-indigo-600 group"
                >
                  <Palette className="h-4 w-4 text-indigo-500 shrink-0" />
                  <span className="text-[13px] font-medium flex-1">
                    Criativo com texto
                  </span>
                </button>
              )}
              {onEnhanceImage && (
                <button
                  onClick={onEnhanceImage}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    onEnhanceImage();
                  }}
                  disabled={loadingEnhanceImage}
                  className={cn(
                    "w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md",
                    "text-gray-700 hover:bg-gray-100/80 hover:text-orange-600 group",
                    loadingEnhanceImage && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {loadingEnhanceImage ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 shrink-0" />
                  ) : (
                    <Palette className="h-4 w-4 text-orange-500 shrink-0" />
                  )}
                  <span className="text-[13px] font-medium flex-1">
                    Melhorar imagem
                  </span>
                </button>
              )}
              <button
                onClick={onGenerateVideo}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onGenerateVideo();
                }}
                disabled={loadingVideo}
                className={cn(
                  "w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md",
                  "text-gray-700 hover:bg-gray-100/80 hover:text-pink-600 group",
                  loadingVideo && "opacity-50 cursor-not-allowed",
                )}
              >
                {loadingVideo ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 shrink-0" />
                ) : (
                  <Video className="h-4 w-4 text-pink-500 shrink-0" />
                )}
                <span className="text-[13px] font-medium flex-1">
                  Gerar vídeo
                </span>
              </button>
            </div>

            {/* ESTRATÉGIA */}
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 px-2 py-1 mt-1 border-t border-purple-50/50">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  Estratégia
                </span>
              </div>
              <button
                onClick={onVisualSuggestions}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onVisualSuggestions();
                }}
                className={cn(
                  "w-full px-2.5 py-1.5 text-left transition-colors flex items-center gap-2 rounded-md",
                  "text-gray-700 hover:bg-gray-100/80 hover:text-teal-600 group",
                )}
              >
                <Eye className="h-4 w-4 text-teal-500 shrink-0" />
                <span className="text-[13px] font-medium flex-1">
                  Sugestões visuais
                </span>
                {hasVisualSuggestions && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-medium text-teal-700"
                    title="Sugestões já geradas"
                  >
                    <CheckCircle2 className="h-3 w-3 text-teal-600" />
                    Geradas
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIOptimizerMenu;
